import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ProgressRing, StatCard, FieldRule, Loading, StatusChip } from "@/components/common";
import { Briefcase, DollarSign, Award, Clock, ScanLine, Wrench, Calculator, PlayCircle, AlertTriangle, ShieldAlert, History } from "lucide-react";
import { toast } from "sonner";

const timeAgo = (iso) => {
  if (!iso) return "";
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
};

const fmtT = (iso) => (iso ? new Date(iso).toLocaleString() : "—");

function CrewTimePay() {
  const [range, setRange] = useState("day");
  const [rows, setRows] = useState(null);
  const [expanded, setExpanded] = useState(false);
  const [open, setOpen] = useState({});

  useEffect(() => {
    let alive = true;
    api.get("/time/pay-report", { params: { range } }).then(({ data }) => { if (alive) setRows(data.employees); });
    return () => { alive = false; };
  }, [range]);

  const shown = rows ? (expanded ? rows : rows.slice(0, 4)) : [];
  return (
    <div className="sg-card p-5 mt-6" data-testid="crew-time-pay-card">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
        <div className="font-head text-lg font-bold flex items-center gap-2"><DollarSign size={18} style={{ color: "var(--accent)" }} /> Crew Time & Pay</div>
        <div className="flex gap-1" data-testid="crew-pay-range-tabs">
          {["day", "week", "month", "year"].map((r) => (
            <button key={r} onClick={() => setRange(r)} data-testid={`crew-pay-range-${r}`}
              className="sg-btn" style={{ minHeight: 30, padding: "3px 12px", textTransform: "uppercase", fontSize: 11,
                background: range === r ? "var(--accent)" : undefined, color: range === r ? "#fff" : undefined,
                borderColor: range === r ? "var(--accent)" : undefined }}>{r}</button>
          ))}
        </div>
      </div>
      {!rows && <div className="text-sm" style={{ color: "var(--txt2)" }}>Loading…</div>}
      {rows && rows.length === 0 && <div className="text-sm" style={{ color: "var(--txt2)" }}>No employees in scope.</div>}
      {rows && rows.length > 4 && (
        <button className="text-xs font-semibold mb-2" style={{ color: "var(--accent)" }} onClick={() => setExpanded((v) => !v)} data-testid={expanded ? "crew-pay-collapse" : "crew-pay-expand"}>
          {expanded ? "Collapse" : `Expand (${rows.length})`}
        </button>
      )}
      <div style={expanded ? { maxHeight: 360, overflowY: "auto" } : undefined} className={expanded ? "pr-1 flex flex-col gap-2" : "flex flex-col gap-2"} data-testid="crew-pay-scroll">
        {shown.map((e) => (
          <div key={e.user_id} className="sg-card-elev p-3" data-testid={`crew-pay-row-${e.user_id}`}>
            <div className="flex items-center justify-between flex-wrap gap-2 cursor-pointer" onClick={() => setOpen((o) => ({ ...o, [e.user_id]: !o[e.user_id] }))} data-testid={`crew-pay-toggle-${e.user_id}`}>
              <div className="text-sm">
                <b>{e.name}</b> <span style={{ color: "var(--txt2)" }}>· {e.role}{e.crew_number ? ` · ${e.crew_number}` : ""}</span>
              </div>
              <div className="flex items-center gap-3 text-xs" style={{ color: "var(--txt2)" }}>
                <span>{e.total_hours}h</span>
                <span className="font-head font-extrabold text-sm" style={{ color: "var(--accent)" }}>${e.estimated_gross}</span>
              </div>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mt-2 text-xs">
              <div><span style={{ color: "var(--txt2)" }}>Hourly </span><b>${e.hourly_pay}</b></div>
              <div><span style={{ color: "var(--txt2)" }}>Prod. Revenue </span><b>${e.production_revenue ?? 0}</b></div>
              <div><span style={{ color: "var(--txt2)" }}>Prod. Bonus </span><b>${e.production_bonus}</b></div>
              <div><span style={{ color: "var(--txt2)" }}>Mem. Revenue </span><b>${e.membership_revenue ?? 0}</b></div>
              <div><span style={{ color: "var(--txt2)" }}>Mem. Bonus </span><b>${e.membership_bonus}</b></div>
              <div><span style={{ color: "var(--txt2)" }}>Est. Gross </span><b style={{ color: "var(--accent)" }}>${e.estimated_gross}</b></div>
            </div>
            {open[e.user_id] && (
              <div className="mt-2 flex flex-col gap-1" data-testid={`crew-pay-entries-${e.user_id}`}>
                <div className="text-[11px] uppercase tracking-wider" style={{ color: "var(--txt2)" }}>Clock In / Out Log</div>
                {e.entries.length === 0 && <div className="text-xs" style={{ color: "var(--txt2)" }}>No clock records in this range.</div>}
                {e.entries.map((en) => (
                  <div key={en.id} className="text-xs p-2 rounded-lg" style={{ background: "var(--bg-elev)" }}>
                    <div><span style={{ color: "var(--txt2)" }}>In:</span> {fmtT(en.clock_in)} <span style={{ color: "var(--txt2)" }}>@ {en.clock_in_location}</span></div>
                    <div><span style={{ color: "var(--txt2)" }}>Out:</span> {fmtT(en.clock_out)} <span style={{ color: "var(--txt2)" }}>@ {en.clock_out_location}</span></div>
                    <div><span style={{ color: "var(--txt2)" }}>Duration:</span> {en.hours}h · <span style={{ color: en.status === "Complete" ? "var(--txt2)" : "#10B981" }}>{en.status || (en.clock_out ? "Complete" : "On clock")}</span></div>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function AlertsCard({ alerts }) {
  const [expanded, setExpanded] = useState(false);
  const shown = expanded ? alerts : alerts.slice(0, 4);
  return (
    <div className="sg-card p-5" data-testid="alerts-card">
      <div className="flex items-center justify-between mb-3">
        <div className="font-head text-lg font-bold flex items-center gap-2"><AlertTriangle size={18} style={{ color: "var(--accent)" }} /> Operational Alerts</div>
        {alerts.length > 4 && (
          <button className="sg-btn" style={{ minHeight: 32, padding: "3px 12px" }} onClick={() => setExpanded((v) => !v)} data-testid={expanded ? "alerts-collapse" : "alerts-view-all"}>
            {expanded ? "Collapse" : "View All"}
          </button>
        )}
      </div>
      {alerts.length === 0 && <div className="text-sm" style={{ color: "var(--txt2)" }}>No alerts.</div>}
      <div style={expanded ? { maxHeight: 300, overflowY: "auto" } : undefined} className={expanded ? "pr-1" : ""} data-testid="alerts-scroll">
        {shown.map((a) => (
          <div key={a.id} className="mb-3" data-testid={`alert-row-${a.id}`}>
            <div className="flex items-center gap-2"><StatusChip status={a.level} /><span className="text-sm font-semibold">{a.title}</span></div>
            <div className="text-xs mt-1" style={{ color: "var(--txt2)" }}>{a.body}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function ActivityLog({ items }) {
  const [expanded, setExpanded] = useState(false);
  const shown = expanded ? items : items.slice(0, 5);
  return (
    <div className="sg-card p-5 mt-6" data-testid="activity-log-card">
      <div className="flex items-center justify-between mb-3">
        <div className="font-head text-lg font-bold flex items-center gap-2"><History size={18} style={{ color: "var(--txt2)" }} /> {expanded ? "Activity History" : "Recent Activity"}</div>
        {items.length > 5 && (
          <button className="sg-btn" style={{ minHeight: 32, padding: "3px 12px" }} onClick={() => setExpanded((v) => !v)} data-testid={expanded ? "activity-collapse" : "activity-view-all"}>
            {expanded ? "Collapse" : "View All"}
          </button>
        )}
      </div>
      {items.length === 0 && <div className="text-sm" style={{ color: "var(--txt2)" }}>No activity yet.</div>}
      <div style={expanded ? { maxHeight: 320, overflowY: "auto" } : undefined} className={expanded ? "pr-1" : ""} data-testid="activity-scroll">
        {shown.map((a) => (
          <div key={a.id} className="flex items-center justify-between py-2 border-b" style={{ borderColor: "var(--brd)" }} data-testid={`activity-row-${a.id}`}>
            <div className="text-sm">
              <span className="font-semibold" style={{ textTransform: "capitalize" }}>{(a.action || "").replace(/_/g, " ")}</span>
              <span style={{ color: "var(--txt2)" }}> · {a.entity}{a.user_name ? ` · ${a.user_name}` : ""}</span>
            </div>
            <span className="text-xs font-mono" style={{ color: "var(--txt2)" }}>{timeAgo(a.created_at)}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function Home() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [d, setD] = useState(null);
  const [clock, setClock] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get("/dashboard/home");
      setD(data);
      setClock(data.clock_status);
    } catch (e) { /* 401 handled globally by interceptor */ }
  };
  useEffect(() => { load(); }, []);

  const toggleClock = async () => {
    const action = clock === "clocked_in" ? "out" : "in";
    await api.post("/time/clock", { action });
    setClock(action === "in" ? "clocked_in" : "clocked_out");
    toast.success(action === "in" ? "Clocked in" : "Clocked out");
  };

  if (!d) return <Loading label="Loading crew status" />;

  const quick = [
    { label: "Today's Jobs", icon: PlayCircle, to: "/today" },
    { label: "New Estimate", icon: Calculator, to: "/estimator" },
    { label: "Shadow Scan", icon: ScanLine, to: "/shadow-scan" },
    { label: "Check Out Gear", icon: Wrench, to: "/equipment" },
  ];

  return (
    <div className="max-w-6xl">
      <div className="mb-6">
        <div className="font-head text-sm uppercase tracking-widest" style={{ color: "var(--accent)" }}>
          {d.crew?.crew_number} · Welcome back
        </div>
        <h1 className="font-head text-3xl sm:text-4xl font-extrabold">{d.employee.name}</h1>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
        {/* Revenue ring */}
        <div className="sg-card p-6 flex flex-col items-center justify-center lg:col-span-1" data-testid="home-revenue-card">
          <div className="font-head text-xs uppercase tracking-widest mb-3" style={{ color: "var(--txt2)" }}>Today</div>
          <ProgressRing value={d.collected} target={d.target} />
          <div className="mt-4 text-center">
            <div className="font-head text-2xl font-extrabold" style={{ color: d.remaining > 0 ? "var(--accent)" : "#10B981" }}>
              {d.remaining > 0 ? `$${d.remaining.toLocaleString()} TO GO` : "STANDARD MET"}
            </div>
            <div className="text-xs uppercase tracking-wider" style={{ color: "var(--txt2)" }}>Six-Figure Standard</div>
          </div>
        </div>

        {/* Stats + clock */}
        <div className="lg:col-span-2 grid grid-cols-2 sm:grid-cols-2 gap-4">
          <StatCard label="Jobs Completed" value={d.jobs_completed} icon={Briefcase} testid="stat-jobs" />
          <StatCard label="Average Ticket" value={`$${d.avg_ticket}`} icon={DollarSign} accent testid="stat-avg" />
          <StatCard label="Memberships Sold" value={d.memberships_sold} icon={Award} testid="stat-memberships" />
          <div className="sg-card p-4 sm:p-5 flex flex-col justify-between" data-testid="clock-card">
            <div className="flex items-center justify-between">
              <span className="text-xs uppercase tracking-wider" style={{ color: "var(--txt2)" }}>Clock Status</span>
              <Clock size={18} style={{ color: "var(--txt2)" }} />
            </div>
            <StatusChip status={clock === "clocked_in" ? "ready" : "unpaid"} label={clock === "clocked_in" ? "On the Clock" : "Clocked Out"} testid="clock-chip" />
            <button className={`sg-btn ${clock === "clocked_in" ? "" : "sg-btn-accent"} mt-2`} onClick={toggleClock} data-testid="clock-toggle">
              {clock === "clocked_in" ? "Clock Out" : "Clock In"}
            </button>
          </div>
        </div>
      </div>

      {/* Quick actions */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4 mt-6">
        {quick.map((q) => (
          <button key={q.label} onClick={() => navigate(q.to)} data-testid={`quick-${q.label.replace(/\s/g, "-").toLowerCase()}`}
            className="sg-card p-5 flex flex-col items-start gap-3 hover:border-[var(--accent)]" style={{ transition: "border-color .12s" }}>
            <q.icon size={26} style={{ color: "var(--accent)" }} />
            <span className="font-head text-lg font-bold">{q.label}</span>
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6 mt-6">
        <div className="lg:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-4">
          {/* Certifications */}
          <div className="sg-card p-5" data-testid="cert-card">
            <div className="font-head text-lg font-bold mb-3">Certification Status</div>
            {d.certifications.length === 0 && <div className="text-sm" style={{ color: "var(--txt2)" }}>No certifications on record.</div>}
            <div className="flex flex-col gap-2">
              {d.certifications.map((c) => (
                <div key={c.id} className="flex items-center justify-between">
                  <span className="text-sm">{c.name}</span><StatusChip status="certified" />
                </div>
              ))}
            </div>
          </div>
          {/* Restrictions */}
          <div className="sg-card p-5" data-testid="restrictions-card">
            <div className="font-head text-lg font-bold mb-3 flex items-center gap-2"><ShieldAlert size={18} style={{ color: "var(--warn)" }} /> Training Restrictions</div>
            {d.restrictions.length === 0 && <div className="text-sm" style={{ color: "var(--txt2)" }}>No active restrictions.</div>}
            {d.restrictions.map((r) => (
              <div key={r.id} className="text-sm mb-2 p-2 rounded-lg" style={{ background: "rgba(245,158,11,0.08)", color: "#F59E0B" }}>{r.restriction}</div>
            ))}
          </div>
          {/* Equipment alerts */}
          <div className="sg-card p-5 sm:col-span-2" data-testid="equipment-alerts-card">
            <div className="font-head text-lg font-bold mb-3 flex items-center gap-2"><Wrench size={18} style={{ color: "var(--warn)" }} /> Equipment Alerts</div>
            {d.equipment_alerts.length === 0 && <div className="text-sm" style={{ color: "var(--txt2)" }}>All equipment ready.</div>}
            <div className="flex flex-col gap-2">
              {d.equipment_alerts.map((e) => (
                <div key={e.id} className="flex items-center justify-between">
                  <span className="text-sm">{e.name}</span><StatusChip status={e.status} />
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="flex flex-col gap-4">
          <FieldRule />
          <AlertsCard alerts={d.alerts || []} />
        </div>
      </div>

      {(user.role === "owner" || user.role === "manager") && <CrewTimePay />}

      <ActivityLog items={d.recent_activity || []} />
    </div>
  );
}
